import { aabb } from "../math/aabb.js";
import type { Vec3 } from "../math/vec3.js";
import type { LootSpawnDef, MapBox, MapDef, MaterialId } from "./types.js";

/**
 * "Testfield" — the sandbox, and the only map compiled into `shared/`: a 60 ×
 * 60 m walled yard exercising every movement branch — a 3-step staircase, a 0.4
 * m ledge (under STEP_UP_MAX), a 0.9 m barrier, a thin wall, crates. Metres,
 * floor top at y = 0.
 */

function box(
  minX: number,
  minY: number,
  minZ: number,
  maxX: number,
  maxY: number,
  maxZ: number,
  material: MaterialId,
  colorTint?: number,
): MapBox {
  const b: MapBox = { box: aabb(minX, minY, minZ, maxX, maxY, maxZ), material };
  if (colorTint !== undefined) b.colorTint = colorTint;
  return b;
}

const HALF = 30; // half extent of the floor
const WALL_H = 3;
const WALL_T = 0.5;

const solids: MapBox[] = [
  // --- Floor (explicit: the collision world has no implicit ground plane) ---
  box(-HALF, -1, -HALF, HALF, 0, HALF, "concrete", 0x6f7377),

  // --- Perimeter walls ---
  box(-HALF - WALL_T, 0, -HALF - WALL_T, HALF + WALL_T, WALL_H, -HALF, "brick", 0x8a5a44),
  box(-HALF - WALL_T, 0, HALF, HALF + WALL_T, WALL_H, HALF + WALL_T, "brick", 0x8a5a44),
  box(-HALF - WALL_T, 0, -HALF - WALL_T, -HALF, WALL_H, HALF + WALL_T, "brick", 0x8a5a44),
  box(HALF, 0, -HALF - WALL_T, HALF + WALL_T, WALL_H, HALF + WALL_T, "brick", 0x8a5a44),

  // --- Staircase: three 0.3 m steps rising eastward, then a landing ---
  box(6, 0, -2, 7, 0.3, 2, "stone", 0x9a9a90),
  box(7, 0, -2, 8, 0.6, 2, "stone", 0x9a9a90),
  box(8, 0, -2, 9, 0.9, 2, "stone", 0x9a9a90),
  box(9, 0, -2, 12, 0.9, 2, "stone", 0x9a9a90),

  // --- 0.4 m ledge: step-up territory, approached from the west ---
  box(-6, 0, -2, -3, 0.4, 2, "concrete", 0x7d7d7d),

  // --- 0.9 m barrier: too tall to step, low enough to jump onto ---
  box(-14, 0, 8, -6, 0.9, 8.4, "wood", 0x8b6b3e),

  // --- Cover props ---
  box(-9, 0, -7, -7, 1, -5, "wood", 0x8b6b3e),
  box(4.4, 0, -10.6, 5.6, 0.8, -9.4, "metal", 0x5f6a72),
  box(11.5, 0, 7.5, 12.5, 4, 8.5, "concrete", 0x767676),
  box(13, 0, -15.25, 19, 2.5, -12.75, "metal", 0x3f5d4a),

  // --- Thin wall: 0.15 m thick, regression guard against tunneling ---
  box(20, 0, -6, 20.15, 3, 6, "metal", 0x6b6f74),

  // --- Loot crates: the solids for the two `lootSpawns` below, parked in the
  // far
  // east corners away from every coordinate the determinism tests drive
  // through.
  // 0.7 m tall, so they block rather than being stepped over.
  box(25.5, 0, 25.5, 26.7, 0.7, 26.7, "wood", 0x8b6b3e),
  box(25.5, 0, -26.7, 26.7, 0.7, -25.5, "wood", 0x8b6b3e),
];

/**
 * Two crates matching the last two solids above, so the loot pipeline has
 * something to exercise on the one compiled-in map.
 */
const lootSpawns: LootSpawnDef[] = [
  {
    id: "crate-tools",
    kind: "crate",
    table: "tools",
    box: aabb(25.5, 0, 25.5, 26.7, 0.7, 26.7),
  },
  {
    id: "crate-household",
    kind: "cupboard",
    table: "household",
    box: aabb(25.5, 0, -26.7, 26.7, 0.7, -25.5),
  },
];

const playerSpawns: Vec3[] = [
  { x: -20, y: 0, z: -20 },
  { x: 20, y: 0, z: -20 },
  { x: -20, y: 0, z: 20 },
  { x: 20, y: 0, z: 20 },
];

export const TESTFIELD: MapDef = {
  id: "testfield",
  name: "Testfield",
  bounds: aabb(-35, -5, -35, 35, 25, 35),
  raidLengthSec: 600,
  solids,
  playerSpawns,
  extracts: [
    {
      id: "north-gate",
      name: "North Gate",
      zone: aabb(-3, 0, -28, 3, 3, -24),
      dwellSec: 8,
    },
  ],
  waypoints: {
    nodes: [
      { id: 0, pos: { x: -18, y: 0, z: -18 } },
      { id: 1, pos: { x: 18, y: 0, z: -18 } },
      { id: 2, pos: { x: 18, y: 0, z: 18 } },
      { id: 3, pos: { x: -18, y: 0, z: 18 } },
    ],
    edges: [
      [0, 1],
      [1, 2],
      [2, 3],
      [3, 0],
    ],
  },
  lootSpawns,
};

// ---------------------------------------------------------------------------
// The static registry
// ---------------------------------------------------------------------------

/**
 * Maps COMPILED INTO this package — exactly one, deliberately: real maps are
 * generated by the server and downloaded as JSON (`maps/transfer.ts`). A Map so
 * no id collides with a prototype key.
 */
const STATIC_REGISTRY = new Map<string, MapDef>([[TESTFIELD.id, TESTFIELD]]);

/** Every map shipped inside `@rietz/shared`. Not "every map in the game". */
export const STATIC_MAPS: readonly MapDef[] = [TESTFIELD];

/**
 * The map the server hosts by default — a map REQUEST, not a registry key:
 * "rietz" is generated at raid start, so `hasMap(DEFAULT_MAP_ID)` is false.
 */
export const DEFAULT_MAP_ID = "rietz";

/**
 * Looks up a STATIC map by id. Throws for anything else, "rietz" included;
 * validate with `hasMap` first.
 */
export function getMapById(id: string): MapDef {
  const m = STATIC_REGISTRY.get(id);
  if (m === undefined) throw new Error(`unknown static map id: ${id}`);
  return m;
}

/** Non-throwing lookup, for validating untrusted input. */
export function hasMap(id: string): boolean {
  return STATIC_REGISTRY.has(id);
}
